import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { Button, cn, EmptyState, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { AgentSkillDefinitionSummary } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { CalendarIcon, PlusIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { FilterBar } from '@/components/filter-bar';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { AGENT_SKILLS_PATH, StatusBadge } from '@/pages/skills/agent-skill-form';
import { SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';

export const agentSkillsLoaderParams = [] as const;

export type AgentSkillsLoaderData = {
  readonly definitions: ReadonlyArray<AgentSkillDefinitionSummary>;
  readonly columnPreference: ColumnPreference;
};

export async function agentSkillsLoader(): Promise<AgentSkillsLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminAgentSkills.list(),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.agentSkills),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load agent skills', { status: result.status });
  }

  return { definitions: result.body.data, columnPreference };
}

function useAgentSkillColumns(): ReadonlyArray<OperatorColumn<AgentSkillDefinitionSummary>> {
  const t = useTranslations('pages.admin.skills.agent-skills');
  const intl = useIntl();

  return useMemo(() => {
    const triggersLabel = (definition: AgentSkillDefinitionSummary) =>
      definition.supportedTriggers.map((trigger) => t(`trigger.${trigger}`)).join(', ');

    return [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        cell: (definition) => definition.name,
        sortValue: (definition) => definition.name,
      },
      {
        id: 'key',
        label: t('columns.key'),
        icon: TextIcon,
        cellClassName: 'font-mono text-xs text-text-tertiary',
        cell: (definition) => definition.key,
        sortValue: (definition) => definition.key,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[120px]',
        cell: (definition) => <StatusBadge status={definition.status} label={t(`status.${definition.status}`)} />,
        sortValue: (definition) => t(`status.${definition.status}`),
      },
      {
        id: 'triggers',
        label: t('columns.triggers'),
        icon: TagIcon,
        cellClassName: 'text-text-tertiary',
        cell: triggersLabel,
        sortValue: (definition) => (triggersLabel(definition) === '' ? null : triggersLabel(definition)),
      },
      {
        id: 'lastEdited',
        label: t('columns.last-edited'),
        icon: CalendarIcon,
        cellClassName: 'text-text-tertiary',
        cell: (definition) =>
          definition.lastEditedByName !== null
            ? t('last-edited-by', {
                name: definition.lastEditedByName,
                date: intl.formatDate(definition.lastEditedAt, { dateStyle: 'short' }),
              })
            : t('never-edited'),
        sortValue: (definition) => (definition.lastEditedByName === null ? null : Date.parse(definition.lastEditedAt)),
      },
    ];
  }, [t, intl]);
}

export function AgentSkillsPage() {
  const t = useTranslations('pages.admin.skills.agent-skills');
  const { definitions, columnPreference } = useLoaderData() as AgentSkillsLoaderData;
  const navigate = useNavigate();
  const canManage = useCan('backoffice:product-skill:manage');

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    if (needle === '') return definitions;

    return definitions.filter(
      (definition) => definition.name.toLowerCase().includes(needle) || definition.key.toLowerCase().includes(needle),
    );
  }, [definitions, query]);
  const catalogue = useAgentSkillColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.agentSkills,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={filtered.length} icon={TagIcon} />}
        trailing={
          canManage ? (
            <Button size="sm" onClick={() => navigate(`${AGENT_SKILLS_PATH}/new`)}>
              <PlusIcon className="h-4 w-4" />
              {t('new')}
            </Button>
          ) : undefined
        }
      />
      <FilterBar
        searchValue={query}
        searchPlaceholder={t('search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {filtered.length === 0 ? (
        <div className="p-6">
          <EmptyState title={t('empty')} />
        </div>
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((definition) => (
              <TableRow
                key={definition.key}
                interactive
                onClick={() => navigate(`${AGENT_SKILLS_PATH}/${definition.key}`)}
              >
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(definition)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
